import { useCallback, useMemo } from "react";
import { AgentApiClient } from "@/lib/api/clients/agent-api-client";
import { AgentService } from "@/lib/services/agent-service";
import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
} from "@/lib/types/api/agent-types";
import { PaginatedResponse } from "@/lib/types/api/common-types";
import { PaginationParams } from "@/lib/services/base/service-types";
import { toast } from "@/hooks/use-toast";
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

/**
 * React hook for agent service operations
 * Follows SOC: Separates service logic from UI components
 * Follows DRY: Centralized error handling and service instantiation
 */
export function useAgentService() {
  // Create service instance with dependency injection
  const agentService = useMemo(() => {
    const agentApiClient = new AgentApiClient();
    return new AgentService(agentApiClient);
  }, []);

  const createAgent = useCallback(
    async (data: CreateAgentRequest): Promise<Agent | null> => {
      const result = await agentService.create(data);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to create agent",
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: "Success",
        description: "Agent created successfully",
      });
      return result.data || null;
    },
    [agentService]
  );

  const updateAgent = useCallback(
    async (
      agentId: string,
      data: UpdateAgentRequest
    ): Promise<Agent | null> => {
      const result = await agentService.update(agentId, data);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to update agent",
          variant: "destructive",
        });
        return null;
      }
      toast({
        title: "Success",
        description: "Agent updated successfully",
      });
      return result.data || null;
    },
    [agentService]
  );

  const deleteAgent = useCallback(
    async (agentId: string): Promise<boolean> => {
      const result = await agentService.delete(agentId);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to delete agent",
          variant: "destructive",
        });
        return false;
      }
      toast({
        title: "Success",
        description: "Agent deleted successfully",
      });
      return true;
    },
    [agentService]
  );

  const getAgent = useCallback(
    async (agentId: string): Promise<Agent | null> => {
      const result = await agentService.getById(agentId);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to get agent",
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [agentService]
  );

  const listAgents = useCallback(
    async (
      params?: PaginationParams & { access?: "any" | "chat" }
    ): Promise<PaginatedResponse<Agent> | null> => {
      const result = await agentService.list(params);
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to list agents",
          variant: "destructive",
        });
        return null;
      }
      return result.data || null;
    },
    [agentService]
  );

  const getDefaultAgent = useCallback(async (): Promise<Agent | null> => {
    const result = await agentService.getDefaultAgent();
    if (!result.success) {
      toast({
        title: "Error",
        description: result.error?.message || "Failed to get default agent",
        variant: "destructive",
      });
      return null;
    }
    return result.data || null;
  }, [agentService]);

  const deleteAgentWithNavigation = useCallback(
    async (
      agentId: string,
      router: AppRouterInstance,
      currentPathname: string
    ): Promise<boolean> => {
      const result = await agentService.deleteWithNavigation(
        agentId,
        router,
        currentPathname
      );
      if (!result.success) {
        toast({
          title: "Error",
          description: result.error?.message || "Failed to delete agent",
          variant: "destructive",
        });
        return false;
      }
      toast({
        title: "Success",
        description: "Agent deleted successfully",
      });
      return true;
    },
    [agentService]
  );

  const validateDeletionEligibility = useCallback(
    async (
      agent: Agent | null
    ): Promise<{ canDelete: boolean; reasons: string[] }> => {
      return agentService.validateDeletionEligibility(agent);
    },
    [agentService]
  );

  const assessDeletionImpact = useCallback(
    async (
      agentId: string,
      sessionsCount: number = 0
    ): Promise<{
      estimatedDataToDelete: string[];
      affectedSessions: number;
      warningMessage?: string;
    }> => {
      return agentService.assessDeletionImpact(agentId, sessionsCount);
    },
    [agentService]
  );

  return {
    // Core CRUD operations
    createAgent,
    updateAgent,
    deleteAgent,
    getAgent,
    listAgents,
    getDefaultAgent,

    // deletion methods
    deleteAgentWithNavigation,
    validateDeletionEligibility,
    assessDeletionImpact,
  };
}
